import React, { ChangeEvent, useEffect } from 'react';
import { useFormik } from 'formik';
import type { SubmissionProps } from './types';
import { Button, Form } from 'react-bootstrap';
import './Submission.scss';
import FormInput from 'components/FormInput';
import { FormCaptureInput } from 'components/common/FormCaptureInput';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootStates } from 'redux/store';
import { Rating } from 'components/common/Rating';
import { setSubmissionDetails } from 'redux/conductInspectionSlice';
import { SubmissionSchema } from './validationSchema';
import { useTranslation } from 'react-i18next';
import { DateTimePicker } from 'components/common/DateTimePicker';
import moment from 'moment';

export const Submission: React.FC<SubmissionProps> = ({
  noFooter,
  disabled,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { projectId, inspectionId } = useParams();
  const submissionDetails = useSelector(
    (state: RootStates) => state.conductInspection.submissionDetails,
  );
  const requiredReinspection = useSelector((state: RootStates) =>
    Object.values(
      state.conductInspection?.checklistDetails?.questions ?? {},
    ).some((q) => q.requireReinspection),
  );
  const formik = useFormik({
    initialValues: {
      submissionDocuments: submissionDetails?.submissionDocuments ?? [],
      drawingTitle: submissionDetails?.drawingTitle ?? [],
      doerImages: submissionDetails?.doerImages ?? [],
      generalFeedback: submissionDetails?.generalFeedback ?? '',
      eSign: submissionDetails?.eSign ?? '',
      satisfactionLevel: submissionDetails?.satisfactionLevel ?? 0,
      closeByDate: submissionDetails?.closeByDate ?? null,
    },
    validationSchema: SubmissionSchema,
    onSubmit: (values) => {
      onSubmit?.(values);
    },
  });

  useEffect(() => {
    if (submissionDetails?.eSign && !formik.values.eSign) {
      formik.setFieldValue(
        'submissionDocuments',
        submissionDetails?.submissionDocuments,
      );
      formik.setFieldValue('drawingTitle', submissionDetails?.drawingTitle);
      formik.setFieldValue('doerImages', submissionDetails?.doerImages);
      formik.setFieldValue(
        'generalFeedback',
        submissionDetails?.generalFeedback,
      );
      formik.setFieldValue('eSign', submissionDetails?.eSign);
      formik.setFieldValue(
        'satisfactionLevel',
        submissionDetails?.satisfactionLevel,
      );
      formik.setFieldValue(
        'closeByDate',
        submissionDetails?.closeByDate ?? null,
      );
    }
  }, [submissionDetails, formik]);

  const handleDocumentCapture = (image: string) => {
    const images = [
      ...formik.values.submissionDocuments,
      { docType: 'image', url: image },
    ];

    formik.setFieldValue('submissionDocuments', images);
    dispatch(
      setSubmissionDetails({
        ...submissionDetails,
        submissionDocuments: images,
      }),
    );
  };

  const handleDrawingCapture = (image: string) => {
    const images = [
      ...formik.values.drawingTitle,
      { docType: 'image', url: image },
    ];

    formik.setFieldValue('drawingTitle', images);
    dispatch(
      setSubmissionDetails({
        ...submissionDetails,
        drawingTitle: images,
      }),
    );
  };

  const handleDoerCapture = (image: string) => {
    const images = [
      ...formik.values.doerImages,
      { docType: 'image', url: image },
    ];

    formik.setFieldValue('doerImages', images);
    dispatch(
      setSubmissionDetails({
        ...submissionDetails,
        doerImages: images,
      }),
    );
  };

  const handleRatingChange = (satisfactionLevel: number) => {
    formik.setFieldValue('satisfactionLevel', satisfactionLevel);
    dispatch(
      setSubmissionDetails({
        ...submissionDetails,
        satisfactionLevel,
      }),
    );
  };

  const handleGeneralFeedbackChange = (e: ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue('generalFeedback', e.target.value);
    dispatch(
      setSubmissionDetails({
        ...submissionDetails,
        generalFeedback: e.target.value,
      }),
    );
  };

  const handleSignatureChange = (e: ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue('eSign', e.target.value);
    dispatch(
      setSubmissionDetails({
        ...submissionDetails,
        eSign: e.target.value,
      }),
    );
  };

  const getDeleteImageHandler =
    (key: 'submissionDocuments' | 'drawingTitle' | 'doerImages') =>
    (images: string[]) => {
      const updatedImages = images.map((img) => ({
        docType: 'image',
        url: img,
      }));
      formik.setFieldValue(key, updatedImages);
      dispatch(
        setSubmissionDetails({
          ...submissionDetails,
          [key]: updatedImages,
        }),
      );
    };

  const handleDateChange = (value: Date) => {
    formik.setFieldValue('closeByDate', value.toISOString());
    dispatch(
      setSubmissionDetails({
        ...submissionDetails,
        closeByDate: value.toISOString(),
      }),
    );
  };

  const handleBack = () =>
    navigate(`/project/${projectId}/conduct-inspection/${inspectionId}/2`);

  return (
    <Form
      className="submission-details d-flex flex-column"
      onSubmit={formik.handleSubmit}
    >
      <div className="d-flex flex-column form-content">
        <FormCaptureInput
          limit={3}
          images={
            formik.values.submissionDocuments?.map((img) => img.url) ?? []
          }
          path={`conduct-inspection/${projectId}/submission/documents/${Date.now()}`}
          label={t('label_upload_document')}
          disabled={disabled}
          onCapture={handleDocumentCapture}
          onDelete={getDeleteImageHandler('submissionDocuments')}
        />
        <FormCaptureInput
          label={t('label_upload_drawing_title')}
          images={formik.values.drawingTitle?.map((img) => img.url) ?? []}
          limit={3}
          path={`conduct-inspection/${projectId}/submission/drawing/${
            (formik.values.drawingTitle?.length ?? 0) + 1
          }`}
          disabled={disabled}
          onCapture={handleDrawingCapture}
          onDelete={getDeleteImageHandler('drawingTitle')}
        />
        <FormCaptureInput
          limit={3}
          images={formik.values.doerImages?.map((img) => img.url) ?? []}
          label={t('label_upload_doer_photo')}
          disabled={disabled}
          path={`conduct-inspection/${projectId}/submission/doer/${
            (formik.values.doerImages?.length ?? 0) + 1
          }`}
          onCapture={handleDoerCapture}
          onDelete={getDeleteImageHandler('doerImages')}
        />
        {requiredReinspection && (
          <div className="d-flex mt-2">
            <DateTimePicker
              value={moment(formik.values.closeByDate).toDate()}
              label={t('label_close_by_date')}
              disabled={disabled}
              placement="auto"
              onChange={handleDateChange}
            />
          </div>
        )}

        <FormInput
          name="generalFeedback"
          value={formik.values.generalFeedback}
          label={t('label_generalFeedback')}
          disabled={disabled}
          required={false}
          onChange={handleGeneralFeedbackChange}
        />
        <div className="d-flex justify-space-between gap-1 gap-md-3 flex-column flex-md-row ratings-container">
          <FormInput
            name="signature"
            label={t('label_esign')}
            value={formik.values.eSign}
            touched={formik.touched.eSign}
            error={formik.errors.eSign}
            disabled={disabled}
            onChange={handleSignatureChange}
          />
          <Rating
            label={t('label_inspection_satisfaction_level')}
            value={formik.values.satisfactionLevel}
            error={formik.errors.satisfactionLevel}
            disabled={disabled}
            onChange={handleRatingChange}
          />
        </div>
      </div>
      {!noFooter && (
        <div className="d-flex justify-content-between">
          <Button onClick={handleBack}>{t('btn_back')}</Button>
          <Button type="submit">{t('btn_next')}</Button>
        </div>
      )}
    </Form>
  );
};
